/**
 * useReorderProductSheet Hook
 *
 * Resolves full product data from order history + menu store when a reorder
 * product sheet is opened, manages customization option selection state,
 * computes price, and provides the "Add to Order" action handler.
 *
 * Follows the same customization state pattern as useFavoriteProductSheet.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  useRestaurantStore,
  useCartStore,
  useOrderStore,
  useUIStore,
  formatCurrency,
  OptionRow,
} from '@restaurant/core'
import type {
  SheetContentReorderProductProps,
  SectionProductInformationProps,
  ProductOptionGroup,
  MenuItem,
  SelectedOption,
} from '@restaurant/core'

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Convert `OrderItem.selectedOptions` (SelectedOption[]) into the
 * `Record<string, Set<string>>` format used for customization state.
 *
 * Matches each SelectedOption to its customization group via optionTitle,
 * then resolves individual items by customizationItemId (with a name fallback).
 */
function buildInitialSelectionsFromOrder(
  selectedOptions: SelectedOption[],
  menuItem: MenuItem,
): Record<string, Set<string>> {
  const selections: Record<string, Set<string>> = {}

  if (!selectedOptions || selectedOptions.length === 0 || !menuItem.customizations) {
    return selections
  }

  for (const option of selectedOptions) {
    // Find the customization group by matching optionTitle to customization.name
    const customization = menuItem.customizations.find(
      c => c.name.toLowerCase() === option.optionTitle.toLowerCase(),
    )
    if (!customization) continue

    const optionIds = new Set<string>()
    for (const selectedItem of option.selectedItems) {
      // Try to find option by customizationItemId first, then by name
      const matchedOption =
        customization.options.find(o => o.id === selectedItem.customizationItemId) ||
        customization.options.find(
          o => o.name.toLowerCase() === selectedItem.name.toLowerCase(),
        )
      if (matchedOption) optionIds.add(matchedOption.id)
    }
    if (optionIds.size > 0) selections[customization.id] = optionIds
  }

  return selections
}

// ── Hook ────────────────────────────────────────────────────────────────

export function useReorderProductSheet(): {
  sheetProps: SheetContentReorderProductProps | null
  isOpen: boolean
  onClose: () => void
} {
  // Store selectors
  const isReorderProductSheetOpen = useUIStore(s => s.isReorderProductSheetOpen)
  const selectedReorderProduct = useUIStore(s => s.selectedReorderProduct)
  const closeReorderProductSheet = useUIStore(s => s.closeReorderProductSheet)
  const showToast = useUIStore(s => s.showToast)

  const getMenuItemById = useRestaurantStore(s => s.getMenuItemById)
  const orderHistory = useOrderStore(s => s.orderHistory)
  const addItem = useCartStore(s => s.addItem)

  // Look up the order and the specific order item
  const order = useMemo(
    () =>
      selectedReorderProduct
        ? orderHistory.find(o => o.id === selectedReorderProduct.orderId)
        : undefined,
    [selectedReorderProduct, orderHistory],
  )

  const orderItem = useMemo(
    () =>
      order && selectedReorderProduct
        ? order.items.find(i => i.itemId === selectedReorderProduct.itemId)
        : undefined,
    [order, selectedReorderProduct],
  )

  // Resolve the full MenuItem from the restaurant store
  const menuItem = useMemo(
    () =>
      selectedReorderProduct ? getMenuItemById(selectedReorderProduct.productId) : undefined,
    [selectedReorderProduct, getMenuItemById],
  )

  // ── Customization state ─────────────────────────────────────────────
  const [selectedCustomizations, setSelectedCustomizations] = useState<
    Record<string, Set<string>>
  >({})

  // Re-initialise when a new reorder product is opened
  useEffect(() => {
    if (orderItem && menuItem) {
      const initial = buildInitialSelectionsFromOrder(orderItem.selectedOptions, menuItem)
      setSelectedCustomizations(initial)
    }
  }, [orderItem?.itemId, menuItem])

  // Handle customisation option toggling (radio / checkbox)
  const handleOptionChange = useCallback(
    (customizationId: string, optionId: string, selected: boolean, isRadio: boolean) => {
      setSelectedCustomizations(prev => {
        const next = { ...prev }

        if (isRadio) {
          next[customizationId] = new Set([optionId])
        } else {
          const current = new Set(prev[customizationId] || [])
          if (selected) {
            current.add(optionId)
          } else {
            current.delete(optionId)
          }
          next[customizationId] = current
        }

        return next
      })
    },
    [],
  )

  // ── Option groups (for the sheet UI) ────────────────────────────────
  const optionGroups: ProductOptionGroup[] = useMemo(() => {
    if (!menuItem?.customizations) return []

    return menuItem.customizations.map(customization => {
      const isRadio = customization.maxSelections === 1
      const selections = selectedCustomizations[customization.id] || new Set()

      return {
        id: customization.id,
        title: customization.name,
        required: customization.isRequired,
        maxSelections: customization.maxSelections,
        children: React.createElement(
          React.Fragment,
          null,
          ...customization.options.map(option =>
            React.createElement(OptionRow, {
              key: option.id,
              text: `${option.name}${option.price > 0 ? ` (+${formatCurrency(option.price)})` : ''}`,
              optionType: isRadio ? ('radio' as const) : ('checkbox' as const),
              value: option.id,
              name: customization.id,
              selected: selections.has(option.id),
              onChange: (value: string, selected: boolean) =>
                handleOptionChange(customization.id, value, selected, isRadio),
            }),
          ),
        ),
      }
    })
  }, [menuItem, selectedCustomizations, handleOptionChange])

  // ── Price computation ───────────────────────────────────────────────
  const totalPrice = useMemo(() => {
    if (!menuItem) return 0

    // Determine base price — use variant if applicable
    let base = menuItem.price
    const variant = orderItem?.selectedPriceVariant
    if (variant && variant !== 'base' && menuItem.priceVariants?.[variant] != null) {
      base = menuItem.priceVariants[variant] as number
    }

    // Add customization option costs
    let customizationCost = 0
    for (const [groupId, optionIds] of Object.entries(selectedCustomizations)) {
      const group = menuItem.customizations?.find(c => c.id === groupId)
      if (!group) continue
      for (const optId of optionIds) {
        const opt = group.options.find(o => o.id === optId)
        if (opt) customizationCost += opt.price
      }
    }

    return base + customizationCost
  }, [menuItem, selectedCustomizations, orderItem])

  // ── Action handler ─────────────────────────────────────────────────

  const handleAddToOrder = useCallback(() => {
    if (!menuItem) return

    const customizations = Object.entries(selectedCustomizations).flatMap(
      ([customizationItemId, optionIds]) =>
        Array.from(optionIds).map(selectedOptionId => ({
          customizationItemId,
          selectedOptionId,
        })),
    )

    const variant = orderItem?.selectedPriceVariant
    addItem(
      menuItem,
      1,
      customizations,
      variant && variant !== 'base' ? { selectedPriceVariant: variant } : undefined,
    )

    closeReorderProductSheet()
    showToast('Added to order')
  }, [menuItem, selectedCustomizations, orderItem, addItem, closeReorderProductSheet, showToast])

  // ── Build return value ──────────────────────────────────────────────

  if (!selectedReorderProduct || !orderItem || !menuItem) {
    return {
      sheetProps: null,
      isOpen: isReorderProductSheetOpen,
      onClose: closeReorderProductSheet,
    }
  }

  const sectionProductInformation: SectionProductInformationProps = {
    productName: orderItem.menuItem.name,
    description: menuItem.description || '',
    currentPrice: formatCurrency(totalPrice),
    imageUrl: orderItem.menuItem.imageUrl || menuItem.imageUrl,
  }

  return {
    sheetProps: {
      sectionProductInformation,
      optionGroups,
      addToOrderPrice: formatCurrency(totalPrice),
      onAddToOrder: handleAddToOrder,
    },
    isOpen: isReorderProductSheetOpen,
    onClose: closeReorderProductSheet,
  }
}
